import { useQuery } from '@tanstack/react-query';

import axiosInstance from './axios';
import { getConfig } from './config';
import { APIResponse } from './types';
import dayjs from 'dayjs';

type ReportType =
  | 'ATRISKATT'
  | 'ATTPERMONTH'
  | 'ATTENDANCEHISTORY'
  | 'BASICCHURN'
  | 'BASICCONVERSIONS'
  | 'BASICCONVERSIONSSUMMARY'
  | 'FORECASTED'
  | 'LASTMONTH'
  | 'LOCMEMBCOUNT'
  | 'MEMBSESSATT'
  | 'MTDREVENUE'
  | 'NETREVENUE'
  | 'NEWCONTACTS'
  | 'NEWSALES'
  | 'PAYMENTSFAILURE'
  | 'WEEKLYATT';

type GenericReportRequest = {
  locationId: number;
  reportDateFrom: string;
  reportDateTo: string;
  report: ReportType;
  filter: string;
  memberId?: string;
};

type ReportRequest = {
  locationId: number;
  reportDateFrom: string;
  reportDateTo: string;
  filter: string;
  isDea?: boolean;
};

type ReportListQueryResponse = {
  active: boolean;
  dashboard_id: string;
  description: string;
  name: string;
  report_id: number;
  sheet_id: number;
  visual_id: number;
};

type ReportDetailQueryResponse = {
  location_id: number;
  report_url: string;
  timezone_offset: number;
  dashboard_url: string;
  active: boolean;
  name: string;
};

const getReport = (params: GenericReportRequest) => {
  const config = getConfig();

  return axiosInstance
    .get<APIResponse>(`${config.domains.reports}/reports/locations/${params.locationId}/${params.report}`, {
      params: {
        report_date_from: params.reportDateFrom,
        report_date_to: params.reportDateTo,
        filter: params.filter,
        member_id: params.memberId,
      },
    })
    .then(({ data }) => data?.data);
};

const getReportV2 = (params: GenericReportRequest) => {
  const config = getConfig();
  const domain = config.domains.reports.replace('v1', 'v2');

  return axiosInstance
    .get<APIResponse>(`${domain}/reports/locations/${params.locationId}/${params.report}`, {
      params: {
        report_date_from: params.reportDateFrom,
        report_date_to: params.reportDateTo,
        filter: params.filter,
        member_id: params.memberId,
      },
    })
    .then(({ data }) => data?.data);
};

const getAtRiskAttendance = (params: ReportRequest) => getReport({ ...params, report: 'ATRISKATT' });

export const useGetAtRiskAttendanceQuery: any = (params: ReportRequest) =>
  useQuery({
    queryKey: ['ATRISKATT', params],
    queryFn: () => getAtRiskAttendance(params),
  });

const getContactAttendance = (params: ReportRequest) => getReport({ ...params, report: 'ATTPERMONTH' });

export const useGetContactAttendanceQuery: any = (params: ReportRequest) =>
  useQuery({
    queryKey: ['ATTPERMONTH', params],
    queryFn: () => getContactAttendance(params),
    enabled: !!params.filter,
  });

const getBasicChurn = (params: ReportRequest) => getReport({ ...params, report: 'BASICCHURN' });

export const useGetBasicChurnQuery: any = (params: ReportRequest) =>
  useQuery({
    queryKey: ['BASICCHURN', params],
    queryFn: () => getBasicChurn(params),
    enabled:
      !!params.reportDateFrom &&
      !!params.reportDateTo &&
      dayjs(params.reportDateFrom).isSameOrBefore(params.reportDateTo),
  });

const getBasicConversion = (params: ReportRequest) => getReport({ ...params, report: 'BASICCONVERSIONS' });

export const useGetBasicConversionQuery: any = (params: ReportRequest) =>
  useQuery({
    queryKey: ['BASICCONVERSIONS', params],
    queryFn: () => getBasicConversion(params),
    enabled: !!params.reportDateFrom && !!params.reportDateTo,
  });

const getBasicConversionSummary = (params: ReportRequest) =>
  getReport({ ...params, report: 'BASICCONVERSIONSSUMMARY' });

export const useGetBasicConversionSummaryQuery: any = (params: ReportRequest) =>
  useQuery({
    queryKey: ['BASICCONVERSIONSSUMMARY', params],
    queryFn: () => getBasicConversionSummary(params),
    enabled: !!params.reportDateFrom && !!params.reportDateTo,
  });

const getLocationMemberCounters = (params: ReportRequest) => getReport({ ...params, report: 'LOCMEMBCOUNT' });

export const useGetLocationMemberCountersQuery: any = (params: ReportRequest) =>
  useQuery({
    queryKey: ['LOCMEMBCOUNT', params],
    queryFn: () => getLocationMemberCounters(params),
  });

const getMemberSessionAttendance = (params: ReportRequest) => getReport({ ...params, report: 'MEMBSESSATT' });

export const useGetMemberSessionAttendanceQuery: any = (params: ReportRequest) =>
  useQuery({
    queryKey: ['MEMBSESSATT', params],
    queryFn: () => getMemberSessionAttendance(params),
  });

const getMonthToDateRevenue = (params: ReportRequest) => {
  if (params.isDea) {
    return getReportV2({ ...params, report: 'MTDREVENUE' });
  }
  return getReport({ ...params, report: 'MTDREVENUE' });
};

export const useGetMonthToDateRevenueQuery: any = (params: ReportRequest) =>
  useQuery({
    queryKey: ['MTDREVENUE', params],
    queryFn: () => getMonthToDateRevenue(params),
  });



const getNetRevenue = (params: ReportRequest) => getReport({ ...params, report: 'NETREVENUE' });

export const useGetNetRevenueQuery: any = (params: ReportRequest) =>
  useQuery({
    queryKey: ['NETREVENUE', params],
    queryFn: () => getNetRevenue(params),
    enabled:
      !!params.reportDateFrom &&
      !!params.reportDateTo &&
      dayjs(params.reportDateFrom).isSameOrBefore(params.reportDateTo),
  });

const getForecastedRevenue = (params: ReportRequest) => getReport({ ...params, report: 'FORECASTED' });

export const useGetForecastedRevenueQuery: any = (params: ReportRequest) =>
  useQuery({
    queryKey: ['FORECASTED', params],
    queryFn: () => getForecastedRevenue(params),
    enabled: !!params.reportDateFrom && !!params.reportDateTo,
  });

const getLastMonthRevenue = (params: ReportRequest) => getReport({ ...params, report: 'LASTMONTH' });

export const useGetLastMonthRevenueQuery: any = (params: ReportRequest) =>
  useQuery({
    queryKey: ['LASTMONTH', params],
    queryFn: () => getLastMonthRevenue(params),
    enabled: !!params.reportDateFrom && !!params.reportDateTo,
  });

const getNewContacts = (params: ReportRequest) => getReport({ ...params, report: 'NEWCONTACTS' });

export const useGetNewContactsQuery: any = (params: ReportRequest) =>
  useQuery({
    queryKey: ['NEWCONTACTS', params],
    queryFn: () => getNewContacts(params),
  });
const getNewSales = (params: ReportRequest) => getReport({ ...params, report: 'NEWSALES' });

export const useGetNewSalesQuery: any = (params: ReportRequest) =>
  useQuery({
    queryKey: ['NEWSALES', params],
    queryFn: () => getNewSales(params),
    enabled:
      !!params.reportDateFrom &&
      !!params.reportDateTo &&
      dayjs(params.reportDateFrom).isSameOrBefore(params.reportDateTo),
  });

const getAttendanceActivity = (params: ReportRequest) => getReport({ ...params, report: 'ATTENDANCEHISTORY' });

export const useGetAttendanceActivityQuery: any = (params: ReportRequest) =>
  useQuery({
    queryKey: ['ATTENDANCEHISTORY', params],
    queryFn: () => getAttendanceActivity(params),
    enabled:
      !!params.reportDateFrom &&
      !!params.reportDateTo &&
      dayjs(params.reportDateFrom).isSameOrBefore(params.reportDateTo),
  });

/////
const getPaymentFailure = (params: ReportRequest) => getReport({ ...params, report: 'PAYMENTSFAILURE' });

export const useGetPaymentFailureQuery: any = (params: ReportRequest) =>
  useQuery({
    queryKey: ['PAYMENTSFAILURE', params],
    queryFn: () => getPaymentFailure(params),
    enabled:
      !!params.reportDateFrom &&
      !!params.reportDateTo &&
      dayjs(params.reportDateFrom).isSameOrBefore(params.reportDateTo),
  });

////

const getQSReportList = ({ locationId }: { locationId: number }) => {
  const config = getConfig();

  return axiosInstance
    .get<APIResponse<ReportListQueryResponse[]>>(`${config.domains.auth}/admin/locations/${locationId}/reports`)
    .then(({ data }) => data?.data);
};

export const useGetQSReportListQuery = (params: { locationId: number }) =>
  useQuery({
    queryKey: ['QSreport', params],
    queryFn: () => getQSReportList(params),
    enabled: Boolean(params.locationId),
  });

const getQSReport = ({ locationId, reportId }: { locationId: number; reportId: number }) => {
  const config = getConfig();

  return axiosInstance
    .get<
      APIResponse<ReportDetailQueryResponse>
    >(`${config.domains.auth}/admin/locations/${locationId}/reports/${reportId}/url`)
    .then(({ data }) => data?.data);
};

export const useGetQSReportQuery = (params: { locationId: number; reportId: number }, hold: boolean = false) =>
  useQuery({
    queryKey: ['QSreport', params],
    queryFn: () => getQSReport(params),
    enabled: !hold && Boolean(params.locationId) && Boolean(params.reportId),
  });
